import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Avatar,
  Drawer,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useTheme } from "@mui/material/styles";
import { useSocket } from "../../socket/SocketContext";
import {
  ExpandLessRounded as ExpandLessRoundedIcon,
  ExpandMoreRounded as ExpandMoreRoundedIcon,
  LogoutRounded as LogoutRoundedIcon,
  MenuRounded as MenuRoundedIcon,
} from "@mui/icons-material";
import "./dashboard-redesign.css";
import { normalizeRole, ROLES } from "../../utils/rbacHelper";
import { useWhiteLabelRuntime } from "../../modules/whitelabel/runtime/WhiteLabelRuntimeProvider";

export default function GlobalDashboardSidebar({
  items = [],
  profileName = "User",
  roleLabel = "User",
  onLogout,
  compactOnDesktop = true,
}) {
  const theme = useTheme();
  const runtimeBrand = useWhiteLabelRuntime();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const shouldCompactOnDesktop = !isMdDown && compactOnDesktop;
  const currentUser = useSelector((state) => state.auth.user);
  const { socket, connected } = useSocket();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [expandedGroups, setExpandedGroups] = useState(() => {
    const next = {};
    items.forEach((item) => {
      if (item.children?.length && (item.defaultExpanded || item.children.some((child) => child.active))) {
        next[item.key] = true;
      }
    });
    return next;
  });

  const normalizedItems = useMemo(
    () =>
      items.map((item) => ({
        ...item,
        badge: item.key === "notifications" ? unreadNotificationCount : item.badge,
        hasActiveChild: Array.isArray(item.children) ? item.children.some((child) => child.active) : false,
      })),
    [items, unreadNotificationCount]
  );

  const fallbackRemainingPurchaseTokens = useMemo(() => {
    const summary = currentUser?.licenseSummary || {};
    return Math.max(
      0,
      Number(
        summary?.yearlyAvailableLicenses ??
          summary?.totalRemainingCredits ??
          currentUser?.remainingTokens ??
          currentUser?.credits?.balance ??
          0
      ) || 0
    );
  }, [currentUser]);

  const shouldShowDealerTokenBalance =
    normalizeRole(currentUser?.role) === ROLES.DEALER;
  const [remainingPurchaseTokens, setRemainingPurchaseTokens] = useState(
    fallbackRemainingPurchaseTokens
  );

  useEffect(() => {
    setRemainingPurchaseTokens(fallbackRemainingPurchaseTokens);
  }, [fallbackRemainingPurchaseTokens]);

  useEffect(() => {
    if (!shouldShowDealerTokenBalance || !backendUrl) return undefined;

    const token = localStorage.getItem("token");
    if (!token) return undefined;

    let isActive = true;

    const loadRemainingPurchaseTokens = async () => {
      try {
        const response = await axios.get(`${backendUrl}/auth/profile`, {
          headers: { "x-auth-token": token },
        });

        const profile = response?.data || {};
        const summary = profile?.licenseSummary || {};
        const nextBalance = Math.max(
          0,
          Number(
            summary?.yearlyAvailableLicenses ??
              summary?.totalRemainingCredits ??
              profile?.remainingTokens ??
              profile?.credits?.balance ??
              0
          ) || 0
        );

        if (isActive) {
          setRemainingPurchaseTokens(nextBalance);
        }
      } catch (_) {
        if (isActive) {
          setRemainingPurchaseTokens(fallbackRemainingPurchaseTokens);
        }
      }
    };

    loadRemainingPurchaseTokens();

    return () => {
      isActive = false;
    };
  }, [
    backendUrl,
    fallbackRemainingPurchaseTokens,
    shouldShowDealerTokenBalance,
  ]);

  useEffect(() => {
    if (!backendUrl) return undefined;
    const token = localStorage.getItem("token") || localStorage.getItem("authToken");
    if (!token) return undefined;

    let active = true;
    const headers = { Authorization: `Bearer ${token}` };
    const loadUnreadCount = async () => {
      try {
        const response = await axios.get(`${backendUrl}/notificationswebapp/unread-count`, { headers });
        if (active) setUnreadNotificationCount(Math.max(0, Number(response?.data?.count) || 0));
      } catch (_) {
        if (active) setUnreadNotificationCount(0);
      }
    };

    loadUnreadCount();
    const interval = window.setInterval(loadUnreadCount, 30000);
    const refreshFromRealtime = () => loadUnreadCount();
    if (socket && connected) {
      socket.on("notification", refreshFromRealtime);
      socket.on("reminder:due", refreshFromRealtime);
      socket.on("alarm:new", refreshFromRealtime);
    }

    return () => {
      active = false;
      window.clearInterval(interval);
      if (socket) {
        socket.off("notification", refreshFromRealtime);
        socket.off("reminder:due", refreshFromRealtime);
        socket.off("alarm:new", refreshFromRealtime);
      }
    };
  }, [backendUrl, connected, socket]);

  useEffect(() => {
    setExpandedGroups((prev) => {
      const next = {};
      normalizedItems.forEach((item) => {
        if (!item.children?.length) return;
        const hasUserState = Object.prototype.hasOwnProperty.call(prev, item.key);
        const shouldExpand = hasUserState
          ? Boolean(prev[item.key])
          : Boolean(item.defaultExpanded || item.hasActiveChild);
        next[item.key] = shouldExpand;
      });
      return next;
    });
  }, [normalizedItems]);

  const toggleGroup = (key) => {
    setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const sidebar = (
    <aside className={`dashboard-sidebar ${shouldCompactOnDesktop ? "is-compact-desktop" : ""}`}>
      <div className="dashboard-sidebar__brand">
        <div className="dashboard-sidebar__logo">
          <div className="dashboard-sidebar__logo-mark">
            <img
              src={runtimeBrand.logoUrl || "/thirdeye-logo.png"}
              alt={runtimeBrand.brandTitle || "Third Eye Telematics"}
              style={{ width: 26, height: 26, objectFit: "contain" }}
            />
          </div>
            <div>
              <div className="dashboard-sidebar__brand-title">
                {runtimeBrand.brandTitle || "Third Eye Telematics"}
              </div>
              <div className="dashboard-sidebar__brand-subtitle">
                {runtimeBrand.brandSubtitle || "Track. Monitor. Protect."}
              </div>
            </div>
        </div>
      </div>

      <nav className="dashboard-sidebar__nav">
        {normalizedItems.map((item) => {
          const expanded = Boolean(expandedGroups[item.key]);
          const isActive = item.active || item.hasActiveChild;
          const hasChildren = Array.isArray(item.children) && item.children.length > 0;

          return (
            <div key={item.key} className={`dashboard-sidebar__group ${expanded ? "is-expanded" : ""}`}>
              <button
                type="button"
                className={`dashboard-sidebar__item ${isActive ? "is-active" : ""}`}
                onClick={() => {
                  if (hasChildren) {
                    toggleGroup(item.key);
                    return;
                  }
                  setMobileSidebarOpen(false);
                  if (typeof item.onClick === "function") {
                    item.onClick();
                  }
                }}
              >
                <span className="dashboard-sidebar__item-icon">{item.icon}</span>
                <span className="dashboard-sidebar__item-label">{item.label}</span>
                {item.badge > 0 ? (
                  <span className="dashboard-sidebar__item-badge" aria-label={`${item.badge} unread notifications`}>
                    {item.badge > 99 ? "99+" : item.badge}
                  </span>
                ) : null}
                {hasChildren ? (
                  <span className="dashboard-sidebar__item-caret">
                    {expanded ? <ExpandLessRoundedIcon fontSize="small" /> : <ExpandMoreRoundedIcon fontSize="small" />}
                  </span>
                ) : null}
              </button>

              {hasChildren && expanded ? (
                <div className="dashboard-sidebar__subnav">
                  {item.children.map((child) => (
                    <button
                      key={child.key}
                      type="button"
                      className={`dashboard-sidebar__subitem ${child.active ? "is-active" : ""}`}
                      onClick={() => {
                        setMobileSidebarOpen(false);
                        if (typeof child.onClick === "function") {
                          child.onClick();
                        }
                      }}
                    >
                      <span className="dashboard-sidebar__subdot" />
                      <span className="dashboard-sidebar__subitem-label">{child.label}</span>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </nav>

      <div className="dashboard-sidebar__footer">
        <div className="dashboard-sidebar__user">
          <Avatar className="dashboard-sidebar__avatar">
            {String(profileName || "U").charAt(0).toUpperCase()}
          </Avatar>
          <div className="dashboard-sidebar__user-copy">
            <strong>{profileName || "User"}</strong>
            <span>{roleLabel || "User"}</span>
          </div>
        </div>

        {shouldShowDealerTokenBalance ? (
          <div className="dashboard-sidebar__token-card">
            <span className="dashboard-sidebar__token-label">Remaining Purchase Tokens</span>
            <strong className="dashboard-sidebar__token-value">
              {remainingPurchaseTokens.toLocaleString()}
            </strong>
          </div>
        ) : null}

        <div className="dashboard-sidebar__bottom-links">
          <button
            type="button"
            className="dashboard-sidebar__link"
            onClick={() => {
              if (typeof onLogout === "function") {
                onLogout();
              }
            }}
          >
            <LogoutRoundedIcon fontSize="small" />
            <span>Logout</span>
          </button>
        </div>
      </div>
    </aside>
  );

  if (isMdDown) {
    return (
      <>
        <div className="dashboard-mobile-topbar">
          <div className="dashboard-mobile-topbar__brand">
          <IconButton onClick={() => setMobileSidebarOpen(true)} sx={{ color: "white" }}>
            <MenuRoundedIcon />
          </IconButton>
          <div>
              <div className="dashboard-mobile-topbar__title">
                {runtimeBrand.brandTitle || "Third Eye Telematics"}
              </div>
              <div className="dashboard-mobile-topbar__subtitle">
                {runtimeBrand.brandSubtitle || "Track. Monitor. Protect."}
              </div>
          </div>
        </div>
        </div>
        <Drawer
          open={mobileSidebarOpen}
          onClose={() => setMobileSidebarOpen(false)}
          PaperProps={{ sx: { background: "transparent", boxShadow: "none" } }}
        >
          {sidebar}
        </Drawer>
      </>
    );
  }

  return sidebar;
}
